import React, { useEffect, useState, memo } from 'react';
import styled from 'styled-components';
import { Formik } from 'formik';
import { has, isEmpty, isEqual } from 'lodash';
import { DataGrid } from '@mui/x-data-grid';
import { useParams } from 'react-router-dom';

import Dialog from 'components/ui/Dialog';
import Form from 'components/ui/Form';
import { Switch as MuiSwitch, TextField } from '@material-ui/core';
import { GridContainer } from 'styles/form';
import {
  getScheduler,
  updateScheduler,
  createScheduler,
  deleteScheduler,
} from 'api/scheduler';
import SchedulerLinkField from 'components/general/SchedulerLinkField';
import { border } from 'variables/general';
import ModalDetailStat from 'components/general/ModalDetailStat';
import { getOrganizations, showOrganization } from 'api/organization';
import { formatParticipationRate } from 'helpers/format-string';
import AutoComplete from 'components/ui/AutoComplete';
import { showClinic } from 'api/clinic';
import { useSnackBar } from 'context/SnackBarContext';
import { columns } from './scheduler-data-table.config';
import { initialValues, validationSchema } from '../scheduler.config';
import {
  HeaderRight,
  StickyHeader,
  MoreMenu,
  MoreMenuOption,
  Header,
} from './styles';
import IconButton from '@material-ui/core/IconButton';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import SchedulerExportModal from './SchedulerExportModal';

const Details = styled.div`
  display: flex;
  padding: 1.5em 0;
  border-bottom: ${border};
`;

const Title = styled.h4`
  padding-bottom: 0;
  border-bottom: 0;
`;

export const FormSectionTitle = styled.h4`
  margin-bottom: 1.5em;
  margin-top: 3em;
`;

const DateOfBirthRequired = styled.div`
  .MuiSwitch-root {
    left: -0.7em;
    margin-top: 0.5em;
  }
`;

const Label = styled.h6`
  margin-top: 1em;
`;

const Scheduler = memo(
  ({ modalOpen, handleModalClose, blockRouteChange, setBlockRouteChange }) => {
    const { id } = useParams();

    const [scheduler, setScheduler] = useState({});
    const [organizations, setOrganizations] = useState([]);
    const [organization, setOrganization] = useState({});
    const [relatedClinics, setRelatedClinics] = useState();
    const [pageSize, setPageSize] = React.useState(5);
    const { setMessage, setOpenSnackBar, setSeverity } = useSnackBar();
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);

    const [exportModalOpen, setExportModalOpen] = useState(false);

    const {
      name: schedulerName,
      link,
      clinics_number: clinicsNumber,
      slots,
      booked,
      rate_participation: rateParticipation,
      relationShips,
    } = scheduler;

    const { clinics } = relationShips || {};

    const { name: orgName } = organization || {};

    useEffect(async () => {
      if (id) {
        const { data } = await getScheduler(id, true);

        setScheduler(data.data);
      }
    }, []);
    console.log(scheduler);

    useEffect(async () => {
      if (!isEmpty(scheduler)) {
        const { organization_id: orgId } = scheduler;
        const { data } = await showOrganization(orgId);

        setOrganization(data.data);
      }
    }, [scheduler]);

    useEffect(async () => {
      const { data } = await getOrganizations(0, 10000);

      setOrganizations(data.data);
    }, []);

    useEffect(async () => {
      if (clinics && !relatedClinics) {
        const promise = await Promise.all(
          clinics[0].map(async ({ id: clinicId }) => {
            const clinic = await showClinic(clinicId);

            return clinic.data.data;
          }),
        );

        setRelatedClinics(promise);
      }
    }, [clinics]);

    const handleDelete = async () => {
      try {
        await deleteScheduler(id);
        setMessage('Scheduler deleted');
        setSeverity('success');
      } catch (error) {
        console.error(error);
        setMessage('Something went wrong. Failed to delete scheduler.');
        setSeverity('error');
      }

      setOpenSnackBar(true);
      handleModalClose(true);
    };

    const handleSave = async (values) => {
      if (!isEmpty(scheduler)) {
        try {
          await updateScheduler(id, values);

          setMessage('Saved changes');
          setSeverity('success');
        } catch (error) {
          console.error(error);
          setMessage('Something went wrong. Failed to save changes.');
          setSeverity('error');
        }
      } else {
        try {
          await createScheduler(values);
          setMessage('Scheduler created');
          setSeverity('success');
        } catch (error) {
          console.error(error);
          setMessage('Something went wrong. Failed to create scheduler.');
          setSeverity('error');
        }
      }

      setOpenSnackBar(true);
      handleModalClose(true);
    };
    const toggleMoreMenu = () => {
      setMoreMenuOpen(!moreMenuOpen);
    };
    const handleModalOpen = () => {
      setExportModalOpen(true);
    };
    return (
      <Formik
        initialValues={initialValues(scheduler)}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSave}
        blockRouteChange={blockRouteChange}
      >
        {({
          values,
          errors,
          setFieldValue,
          handleChange,
          submitForm,
          touched,
        }) => {
          const { name, organizationId, showBirthDate, customField } = values;

          const initValues = initialValues(scheduler);

          const unsavedChange = !isEqual(
            {
              ...values,
              organizationId: Number(organizationId),
            },
            {
              ...initValues,
              organizationId: Number(initValues.organizationId),
            },
          );

          if (unsavedChange) {
            setBlockRouteChange(true);
          } else {
            setBlockRouteChange(false);
          }

          return (
            <Dialog
              open={modalOpen}
              handleClose={handleModalClose}
              submitForm={submitForm}
              typeText="Scheduler"
              handleDelete={handleDelete}
              disableTopPadding
              maxWidth="md"
              blockRouteChange={blockRouteChange}
            >
              <SchedulerExportModal
                scheduler={scheduler}
                schedulerId={scheduler.id}
                exportModalOpen={exportModalOpen}
                setExportModalOpen={setExportModalOpen}
              />
              {!isEmpty(scheduler) && (
                <>
                  <Header>
                    <Title>{schedulerName}</Title>
                    <IconButton component="span" onClick={toggleMoreMenu}>
                      <MoreVertIcon />

                      {moreMenuOpen && (
                        <ClickAwayListener onClickAway={toggleMoreMenu}>
                          <MoreMenu>
                            <MoreMenuOption onClick={handleModalOpen}>
                              <SaveAltIcon fontSize="sm" />
                              Export Scheduler
                            </MoreMenuOption>
                          </MoreMenu>
                        </ClickAwayListener>
                      )}
                    </IconButton>
                  </Header>

                  <Details>
                    <ModalDetailStat label="Organization" value={orgName} />
                    <ModalDetailStat label="Clinics" value={clinicsNumber} />
                    <ModalDetailStat label="Slots" value={slots} />
                    <ModalDetailStat label="Booked" value={booked} />
                    <ModalDetailStat label="Vacancies" value={slots - booked} />
                    <ModalDetailStat
                      label="Participation"
                      value={formatParticipationRate(rateParticipation)}
                    />
                  </Details>
                </>
              )}

              <FormSectionTitle>Scheduler</FormSectionTitle>
              <Form fullWidth>
                <GridContainer>
                  <TextField
                    color="primary"
                    error={touched.name && has(errors, 'name')}
                    helperText={touched.name && errors.name}
                    label="Name"
                    name="name"
                    onChange={handleChange}
                    value={name}
                    variant="outlined"
                    required
                  />

                  <AutoComplete
                    data={organizations}
                    label="Organization"
                    name="organizationId"
                    setFieldValue={setFieldValue}
                    value={organizationId}
                    groupBy="name"
                    getOptionLabel={({ name }) => name || ''}
                    selection={organization}
                    error={
                      touched.organizationId && has(errors, 'organizationId')
                    }
                    touched={touched}
                    required
                    bottom="-1"
                  />

                  <SchedulerLinkField link={link} />

                  <TextField
                    color="primary"
                    error={touched.customField && has(errors, 'customField')}
                    fullWidth
                    helperText={touched.customField && errors.customField}
                    label="Custom Field Name"
                    name="customField"
                    onChange={handleChange}
                    value={customField}
                    type="customField"
                    variant="outlined"
                    autoComplete="chrome-off"
                  />

                  <DateOfBirthRequired>
                    <Label>Date of Birth Required</Label>
                    <MuiSwitch
                      checked={showBirthDate}
                      name="showBirthDate"
                      onChange={handleChange}
                      color="primary"
                      value={showBirthDate}
                    />
                  </DateOfBirthRequired>
                </GridContainer>
              </Form>

              {relatedClinics && (
                <>
                  <FormSectionTitle>Clinics</FormSectionTitle>
                  <div style={{ height: 400, width: '100%' }}>
                    <DataGrid
                      pageSize={pageSize}
                      onPageSizeChange={(newPageSize) =>
                        setPageSize(newPageSize)
                      }
                      rowsPerPageOptions={[5, 10]}
                      pagination
                      rows={relatedClinics}
                      columns={columns}
                      disableSelectionOnClick
                    />
                  </div>
                </>
              )}
            </Dialog>
          );
        }}
      </Formik>
    );
  },
);

export default Scheduler;
